import {
  ApeironInfinityDataGridRef,
  ApeironPagingDataGridRef,
  BattlePassType,
  createStrictContext,
  MayNull,
  useAccountContext
} from '@apeiron/temp'
import { useBattlepassContext } from '@src/contexts/battle/demo/BattlepassContext'
import useGetClaimableRewardTypeInfosByRewardName from '@src/hooks/graphql/useGetClaimableRewardTypeInfosByRewardName'
import useGetCurrentBattlePassKeyBalance from '@src/hooks/graphql/useGetCurrentBattlePassKeyBalance'
import useRandomBattlePassPrize from '@src/hooks/graphql/useRandomBattlePassPrize'
import useFetchBattlePassLootBoxJson from '@src/hooks/json/useFetchBattlePassLootBoxJson'
import { ParsedLootBoxData } from '@src/types/battlePass'
import { RewardInfo } from '@src/types/reward'
import { parseISO } from 'date-fns'
import * as R from 'ramda'
import {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import useAsyncEffect from 'use-async-effect'

const [ContextProvider, useVipPassContext] =
  createStrictContext<Context>('VipPass')

export { useVipPassContext }

export const VipPassProvider = (props: Props) => {
  const { children } = props

  const { isLoggedIn } = useAccountContext()

  const { battlePassSchedules } = useBattlepassContext()

  const { fetch: fetchUserKeys } = useGetCurrentBattlePassKeyBalance()

  const { fetch: getRandomPrize } = useRandomBattlePassPrize()

  const { fetch: getRewardInfo } = useGetClaimableRewardTypeInfosByRewardName()

  const { data: lootBoxJson } = useFetchBattlePassLootBoxJson()

  const [lootBoxData, setLootBoxData] = useState<ParsedLootBoxData[]>([])

  const [currentPrize, setCurrentPrize] = useState<MayNull<RewardInfo>>(null)

  const infiniteDataGridRef = useRef<ApeironInfinityDataGridRef>(null)

  const pagingDataGridRef = useRef<ApeironPagingDataGridRef>(null)

  const currentBattlePass = useMemo(() => {
    if (R.isNil(battlePassSchedules)) return null

    const now = new Date()

    const battlePass = R.find((schedule: BattlePassType) => {
      if (schedule.battlePassType !== 'PVP') return false

      const startDate = parseISO(schedule.startAt)

      const endDate = parseISO(schedule.endAt)

      // Check if the schedule is either the current or future PVP season
      // startDate <= now && endDate >= now | this is for current season
      // startDate > now | this is for future season
      return (startDate <= now && endDate >= now) || startDate > now
    }, battlePassSchedules)

    return battlePass || null
  }, [battlePassSchedules])

  useAsyncEffect(async () => {
    if (isLoggedIn) {
      const userKeys = await fetchUserKeys()

      const fortuneKeys = currentBattlePass?.fortuneKeys || []

      const lootBoxs = fortuneKeys.map(keyId => {
        const lootBox = lootBoxJson?.find(box => {
          return box.assetName.includes(keyId)
        })

        const userKeyAmount = userKeys?.find(
          key => key.keyAssetName === keyId
        )?.balance

        if (R.isNil(lootBox)) {
          return {
            name: '',
            keyName: '',
            image: '',
            keyImage: '',
            assetName: '',
            keyCount: 0
          }
        }

        return {
          ...lootBox,
          assetName: keyId,
          keyCount: userKeyAmount || 0
        }
      })

      setLootBoxData(lootBoxs)
    }
  }, [isLoggedIn, lootBoxJson, currentBattlePass, currentPrize])

  const refreshDataGrid = useCallback(() => {
    infiniteDataGridRef.current?.refreshData()
    pagingDataGridRef.current?.refreshData()
  }, [])

  const openLootBox = useCallback(
    async (keyAssetName: string) => {
      const prize = await getRandomPrize({ keyAssetName })

      if (R.isNil(prize)) {
        return false
      }

      const prizeDetail = await getRewardInfo(prize?.prizeAssetName)

      if (R.isNil(prizeDetail)) {
        return false
      }

      refreshDataGrid()

      setCurrentPrize({
        ...prizeDetail[0],
        amount: prize.qty.toString()
      })

      return true
    },
    [getRandomPrize, getRewardInfo, refreshDataGrid]
  )

  useEffect(() => {
    refreshDataGrid()
  }, [isLoggedIn, refreshDataGrid])

  return (
    <ContextProvider
      value={{
        lootBoxData,
        currentPrize,
        currentBattlePass,
        infiniteDataGridRef,
        pagingDataGridRef,
        setCurrentPrize,
        openLootBox
      }}
    >
      {children}
    </ContextProvider>
  )
}

type Props = {
  children: ReactNode
}

type Context = {
  lootBoxData: ParsedLootBoxData[]
  currentPrize: MayNull<RewardInfo>
  currentBattlePass: MayNull<BattlePassType>
  infiniteDataGridRef: RefObject<ApeironInfinityDataGridRef>
  pagingDataGridRef: RefObject<ApeironPagingDataGridRef>
  setCurrentPrize: (prize: MayNull<RewardInfo>) => void
  openLootBox: (keyAssetName: string) => Promise<boolean>
}
